import * as React from 'react';
import axios, { AxiosResponse } from 'axios';
import isEmpty from 'lodash/isEmpty';
import { createSSRStore, toSerializedDataProps } from '@awning/archie';
import withSession from '@/src/auth/withSession';
import { AuthServerSidePropsContext, TBasePageProps, TGetServerSideProps } from 'types';
import { TUser, userStoreCreator } from '@/src/shared/userStore';
import ServerAxios from '@/src/ServerAxios';
import {
  EFilterPages,
  FILTER_DEFAULTS,
  filterStoreCreator,
  toServerQueryState,
} from '@/src/Filters';
import {
  MarketInsights,
  Navigation,
  ScheduleAnalysis,
  TMarketInsightsStore,
  marketInsightsStoreCreator,
} from '@/src/market-insights';
import { createHumps } from '@/src/utils';
import { TMarketInsights } from '@/src/market-insights/Charts';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { getPlaceDetails } from '@/src/market-insights/utils';
import { SignupModal } from '@/src/SignupModal';
import { SignupBanner } from '@/src/SignupBanner/SignupBanner';

type MarketInsightsContainerProps = TBasePageProps;

const MarketInsightsHeadTags = React.memo(() => {
  const { query, asPath } = useRouter();

  const getTitleAndDescription = ({ query }: any) => {
    let title = '';
    let description = '';

    const details = getPlaceDetails({ query });
    const { place, locationWithLongState: location, isCity, longStateName } = details;

    // best Airbnbs
    if (asPath.toLowerCase()?.includes('best-airbnb')) {
      const title = `Best Airbnbs in ${location} With Data`;
      return {
        pageTitle: title,
        meta: {
          title,
          description: `Browse the best Airbnb’s in ${location} with data on daily rates, occupancy, reviews, revenue and more.`,
        },
      };
    }
    if (asPath.toLowerCase()?.includes('rates')) {
      const text = isCity ? location : `${place} Zip Code`;
      const title = `Airbnb Average Daiy Rate Rate in ${text}`;
      return {
        pageTitle: title,
        meta: {
          title,
          description: `See average Airbnb average daily rates in ${location} by bedroom count and property type`,
        },
      };
    }

    if (asPath.toLowerCase()?.includes('occupancy')) {
      const text = isCity ? location : `${place} Zip Code`;
      const title = `Average Airbnb Occupancy Rate in ${text}`;
      return {
        pageTitle: title,
        meta: {
          title,
          description: `See average Airbnb occupancy rates in ${location} by bedroom count and property type`,
        },
      };
    }

    if (asPath.toLowerCase()?.includes('revenue')) {
      const text = isCity ? location : `${place} Zip Code`;
      const title = `Average Airbnb Revenue in ${text}`;
      return {
        pageTitle: title,
        meta: {
          title,
          description: `See average Airbnb revenue in ${location} by bedroom count and property type`,
        },
      };
    }

    // CITY
    if (isCity) {
      title = `${[place, longStateName].join(', ')} Airbnb Market Data`;
      description = `Free Airbnb market data, statistics, insights, rankings, rates, occupancy, and revenue in ${place}, ${longStateName}`;

      return { pageTitle: title, meta: { title, description } };
    }

    // ZIPCODE
    title = `Airbnb Market Data in ${[place, longStateName].join(', ')}`;
    description = `Free Airbnb market data, statistics, insights, rankings, rates, occupancy, and revenue in ${place}, ${longStateName}`;

    return { pageTitle: title, meta: { title, description } };
  };

  const {
    pageTitle,
    meta: { title, description },
  } = getTitleAndDescription({ query });

  return (
    <Head>
      <title>{pageTitle ?? title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />

      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@tryawning" />
      <meta name="twitter:creator" content="@tryawning" />
      <meta
        name="twitter:image"
        content={`${process.env.NEXT_PUBLIC_BASE_PATH}/twitter-preview.jpg`}
      />

      <meta property="og:title" content={title} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="628" />
      <meta property="og:type" content="website" />
      <meta property="og:description" content={description} />
      <meta
        property="og:image"
        content={`${process.env.NEXT_PUBLIC_BASE_PATH}/facebook-preview.jpg`}
      />
    </Head>
  );
});

const MarketInsightsContainer: React.FC<
  React.PropsWithChildren<MarketInsightsContainerProps>
> = () => {
  return (
    <>
      <MarketInsightsHeadTags />
      <Navigation render={() => <SignupBanner />} />
      <MarketInsights />

      <ScheduleAnalysis />
      <SignupModal />
    </>
  );
};

const getListings = async (
  geoId: string,
  filterQueryState: any,
  token: string
): Promise<
  AxiosResponse<{
    data: TMarketInsightsStore['listings'];
  }>
> => {
  const headers = token ? { headers: { 'Authorization': `Bearer ${token}` } } : {};
  return ServerAxios.get(`${process.env.NEXT_PUBLIC_AWNING_API_URL}/get_estimate`, {
    ...headers,
    params: {
      geoId,
      filterQueryState,
    },
  });
};

const getMarketInsights = async (
  q: string,
  token: string
): Promise<AxiosResponse<TMarketInsights>> => {
  const vanillaAxios = axios.create();
  const headers = token ? { headers: { 'Authorization': `Bearer ${token}` } } : {};

  const resp = await vanillaAxios.get(`${process.env.NEXT_PUBLIC_AWNING_API_URL}/market_insights`, {
    ...headers,
    params: {
      geoId: `${q.toUpperCase()}`,
    },
  });

  function camelize(str: string) {
    return str
      .replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
        return index === 0 ? word.toLowerCase() : word.toUpperCase();
      })
      .replace(/\s+/g, '');
  }
  const _camelCase = createHumps((s: string) => camelize(s.replace(/_/g, ' ')));
  resp.data = _camelCase(resp.data);
  return resp;
};

const getUser = async (token: string, userProfileId: string) => {
  return ServerAxios.get(`${process.env.NEXT_PUBLIC_AWNING_API_URL}/users/${userProfileId}/`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
};

export const getServerSideProps = withSession(
  async ({
    req,
    query,
  }: AuthServerSidePropsContext): TGetServerSideProps<MarketInsightsContainerProps> => {
    const user = req.session.user;

    let userData: TUser;
    if (user !== undefined) {
      const userDataResponse = await getUser(user.token, user.id);
      userData = userDataResponse?.data;
    }
    const _userStore = createSSRStore(userStoreCreator, api => api.setState({ user: userData }));

    const { q, filterState = '{}' } = query as {
      q: string;
      place: string[];
      filterState: any;
    };

    let listings: TMarketInsightsStore['listings'] = [];
    const marketInsights: TMarketInsights = (await getMarketInsights(q, user?.token)).data ?? {};

    if (isEmpty(marketInsights)) {
      return {
        redirect: {
          destination: 'https://awning.com/airbnb-market-data',
          permanent: false,
        },
      };
    }

    const activePage = EFilterPages.MARKET_INSIGHTS;
    const _filterStore = createSSRStore(filterStoreCreator, api => {
      const state = filterState ? JSON.parse(filterState as string) : undefined;
      api.setState({
        activePage,
        filterState: FILTER_DEFAULTS[activePage].FILTERS,
      });
      api.getState().applyChangeSet(state);
    });

    let filterQueryState = toServerQueryState(
      _filterStore.getState()?.filterState,
      FILTER_DEFAULTS[activePage].INACTIVE_FILTERS,
      FILTER_DEFAULTS[activePage].TRANSFORMS
    );

    if (q) ({ data: listings } = (await getListings(q, filterQueryState, user?.token)).data ?? {});

    const _marketInsightsStore = createSSRStore(marketInsightsStoreCreator, api =>
      api.setState({
        marketInsights,
        q,
        listings,
      })
    );

    return {
      props: {
        // originalUrl: (req.headers['x-middleware-seo-url'] as string) ?? '',
        __storeData: toSerializedDataProps([_userStore, _marketInsightsStore, _filterStore]),
      },
    };
  }
);

export default MarketInsightsContainer;
